import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSets, createSet, updateSet, deleteSet } from '@/api/sets';
import type { CreateSetRequest, UpdateSetRequest } from '@/types/set';

/**
 * Hook to fetch the user's active Sets.
 */
export function useSets(status: string = 'active') {
  return useQuery({
    queryKey: ['sets', { status }],
    queryFn: () => getSets({ status }),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to create a new Set.
 */
export function useCreateSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSetRequest) => createSet(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sets'] });
    },
  });
}

/**
 * Hook to update a Set.
 */
export function useUpdateSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, data }: { setId: string; data: UpdateSetRequest }) =>
      updateSet(setId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sets'] });
    },
  });
}

/**
 * Hook to delete (archive) a Set.
 */
export function useDeleteSet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (setId: string) => deleteSet(setId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sets'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}
